// src/features/roadmap/WorkflowView.jsx
//
// ROADMAP-WORKFLOW-VIEW-S1: Config-driven workflow view for the Roadmap page.
// Accepts a resolved definition object (from workflowDefinitions.js) or null.
// When null → "Not yet mapped" empty state.
// When defined → full pipeline render: stat strip, sources band, step cards,
// amber callout, governance rail, legend.
//
// All colors route through T.*; no raw hex values.

import React from "react";
import { T } from "../../app/tokens.js";
import { ViewScaffold } from "../../components/ViewScaffold.jsx";

// ─── Pure helpers (exported for tests) ────────────────────────────────────────

/**
 * Derive summary counts from a definition's steps and sources.
 * Returns { totalSteps, liveCount, partialCount, sourceGaps, weakestStep }.
 */
export function computeSummaryStats(definition) {
  if (!definition) return null;
  const { steps, sources } = definition;
  const totalSteps  = steps.length;
  const liveCount   = steps.filter((s) => s.status === "live").length;
  const partialCount = steps.filter((s) => s.status === "partial").length;
  const sourceGaps  = sources.filter((s) => s.status !== "live").length;

  // Weakest link: prefer not_built > partial > mock > concept
  const PRIORITY = ["not_built", "concept", "mock", "partial", "live"];
  const weakestStep = steps.slice().sort(
    (a, b) => PRIORITY.indexOf(a.status) - PRIORITY.indexOf(b.status)
  )[0] ?? null;

  return { totalSteps, liveCount, partialCount, sourceGaps, weakestStep };
}

// ─── Status metadata (mirrors FeatureInventoryPanel — kept local so this
//     component stays self-contained and avoids circular imports)
const STEP_STATUS_META = {
  live:      { label: "Live",      color: T.statusLive, bg: T.statusLiveBg },
  partial:   { label: "Partial",   color: T.amber,      bg: T.utilAmber    },
  mock:      { label: "Mock",      color: T.statusMock, bg: T.statusMockBg },
  not_built: { label: "Not built", color: T.errorRed,   bg: T.errorRedBg   },
  concept:   { label: "Concept",   color: T.mid,        bg: T.lightGray    },
};

const SOURCE_STATUS_META = {
  live:    { dot: T.statusLive, label: "Live"    },
  partial: { dot: T.amber,      label: "Partial" },
  gap:     { dot: T.errorRed,   label: "Gap"     },
};

const ACTOR_META = {
  ai:     { label: "AI",     bg: T.purpleBg, color: T.purple  },
  human:  { label: "Human",  bg: T.blueSoft, color: T.blue    },
  system: { label: "System", bg: T.lightGray, color: T.mid    },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatPill({ value, label, accent, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 72 }}>
      <span
        data-testid={testid}
        style={{
          fontSize:   T.fsLg6,
          fontWeight: T.fwDisplay,
          color:      accent || T.dark,
          lineHeight: 1.1,
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize:      T.fsXxs,
          fontWeight:    T.fwSemibold,
          color:         T.mid,
          textTransform: "uppercase",
          letterSpacing: 0.7,
          marginTop:     2,
          textAlign:     "center",
        }}
      >
        {label}
      </span>
    </div>
  );
}

function StatStrip({ stats, objectName }) {
  return (
    <div
      data-testid="workflow-stat-strip"
      style={{
        display:        "flex",
        alignItems:     "center",
        gap:            0,
        background:     T.white,
        border:         `1px solid ${T.border}`,
        borderRadius:   10,
        padding:        "14px 24px",
        marginBottom:   24,
        flexWrap:       "wrap",
      }}
    >
      <StatPill value={objectName} label="Object" accent={T.dark} />
      <Divider />
      <StatPill value={stats.totalSteps} label="Steps" accent={T.dark} testid="stat-total-steps" />
      <Divider />
      <StatPill value={stats.liveCount} label="Live" accent={T.statusLive} testid="stat-live-count" />
      <Divider />
      <StatPill value={stats.partialCount} label="Partial" accent={T.amber} testid="stat-partial-count" />
      <Divider />
      <StatPill value={stats.sourceGaps} label="Source gaps" accent={stats.sourceGaps > 0 ? T.errorRed : T.statusLive} testid="stat-source-gaps" />
      <Divider />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flex: 1, minWidth: 120 }}>
        <span style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.mid, textTransform: "uppercase", letterSpacing: 0.7 }}>
          Weakest link
        </span>
        {stats.weakestStep ? (
          <span style={{ fontSize: T.fsSm, color: T.dark, marginTop: 3 }}>
            <span style={{ fontWeight: T.fwSemibold }}>Step {stats.weakestStep.n}:</span>{" "}
            {stats.weakestStep.title}
            {" — "}
            <StatusPill status={stats.weakestStep.status} small />
          </span>
        ) : (
          <span style={{ fontSize: T.fsSm, color: T.light }}>—</span>
        )}
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        width: 1,
        height: 36,
        background: T.border,
        margin: "0 20px",
        flexShrink: 0,
      }}
    />
  );
}

function StatusPill({ status, small }) {
  const meta = STEP_STATUS_META[status] || STEP_STATUS_META.concept;
  return (
    <span
      data-testid={`status-pill-${status}`}
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        fontSize:     T.fsXs,
        fontWeight:   T.fwSemibold,
        color:        meta.color,
        background:   meta.bg,
        borderRadius: 6,
        padding:      small ? "2px 7px" : "2px 10px",
        whiteSpace:   "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

function ActorBadge({ actor }) {
  const meta = ACTOR_META[actor] || ACTOR_META.system;
  return (
    <span
      data-testid={`actor-badge-${actor}`}
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        fontSize:     T.fsXxs,
        fontWeight:   T.fwSemibold,
        color:        meta.color,
        background:   meta.bg,
        borderRadius: 6,
        padding:      "2px 10px",
        lineHeight:   1.4,
        letterSpacing: 0.2,
        whiteSpace:   "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

function ObjectChip({ label, wired }) {
  return (
    <span
      data-testid={`chip-${label}-${wired ? "wired" : "unwired"}`}
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        gap:          4,
        fontSize:     T.fsXxs,
        fontWeight:   T.fwSemibold,
        color:        wired ? T.blue     : T.mid,
        background:   wired ? T.blueSoft : T.lightGray,
        borderRadius: 6,
        padding:      "2px 10px",
        lineHeight:   1.4,
        whiteSpace:   "nowrap",
      }}
    >
      <span style={{ fontSize: T.fsMin, lineHeight: 1 }}>{wired ? "●" : "○"}</span>
      {label}
    </span>
  );
}

function Sourcesband({ sources }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize:   T.fsSm,
          fontWeight: T.fwSemibold,
          color:      T.mid,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          marginBottom: 10,
        }}
      >
        Information in - sources
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {sources.map((src) => {
          const dotMeta = SOURCE_STATUS_META[src.status] || SOURCE_STATUS_META.gap;
          return (
            <div
              key={src.name}
              data-testid={`source-card-${src.name.toLowerCase().replace(/\s+/g, "-")}`}
              style={{
                display:      "flex",
                alignItems:   "flex-start",
                gap:          10,
                padding:      "12px 16px",
                background:   T.white,
                border:       `1px solid ${T.border}`,
                borderRadius: 8,
                minWidth:     160,
                flex:         "0 0 auto",
              }}
            >
              <span
                style={{
                  width:        10,
                  height:       10,
                  borderRadius: "50%",
                  background:   dotMeta.dot,
                  flexShrink:   0,
                  marginTop:    3,
                }}
              />
              <div>
                <div style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
                  {src.name}
                </div>
                <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 2 }}>
                  {src.subtitle}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepCard({ step }) {
  const isPartial = step.status === "partial";
  return (
    <div
      data-testid={`step-card-${step.n}`}
      style={{
        flex:         "0 0 auto",
        width:        192,
        padding:      "14px 16px",
        background:   isPartial ? T.utilAmber : T.white,
        border:       `1px solid ${isPartial ? T.amber + "66" : T.border}`,
        borderRadius: 8,
        display:      "flex",
        flexDirection: "column",
        gap:          8,
      }}
    >
      {/* Step number + status */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span
          style={{
            fontSize:     T.fsXxs,
            fontWeight:   T.fwBold,
            color:        T.mid,
            letterSpacing: 0.5,
          }}
        >
          STEP {step.n}
        </span>
        <StatusPill status={step.status} small />
      </div>

      {/* Title */}
      <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark, lineHeight: 1.3 }}>
        {step.title}
      </div>

      {/* Description */}
      <div style={{ fontSize: T.fsSm, color: T.mid, lineHeight: 1.5, flex: 1 }}>
        {step.desc}
      </div>

      {/* Actor */}
      <ActorBadge actor={step.actor} />

      {/* Connected object chips */}
      {step.connectedObjects && step.connectedObjects.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
          {step.connectedObjects.map((obj) => (
            <ObjectChip key={obj.label} label={obj.label} wired={obj.wired} />
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectorArrow() {
  return (
    <div
      aria-hidden
      style={{
        flexShrink:  0,
        display:     "flex",
        alignItems:  "center",
        color:       T.border,
        fontSize:    T.fsLg,
        userSelect:  "none",
        paddingTop:  2,
      }}
    >
      →
    </div>
  );
}

function Pipeline({ steps }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize:   T.fsSm,
          fontWeight: T.fwSemibold,
          color:      T.mid,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          marginBottom: 10,
        }}
      >
        Pipeline
      </div>
      <div
        data-testid="workflow-pipeline"
        style={{
          display:    "flex",
          alignItems: "stretch",
          gap:        0,
          overflowX:  "auto",
          paddingBottom: 4,
        }}
      >
        {steps.map((step, idx) => (
          <React.Fragment key={step.n}>
            <StepCard step={step} />
            {idx < steps.length - 1 && <ConnectorArrow />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function InsightCallout({ insight }) {
  return (
    <div
      data-testid="workflow-insight"
      style={{
        display:      "flex",
        alignItems:   "flex-start",
        gap:          12,
        background:   T.utilAmber,
        border:       `1px solid ${T.amber}55`,
        borderRadius: 8,
        padding:      "14px 18px",
        marginBottom: 24,
      }}
    >
      <span style={{ fontSize: T.fsMd, flexShrink: 0 }}>⚠</span>
      <div>
        <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.amberDark, marginBottom: 4 }}>
          Weakest link
        </div>
        <div style={{ fontSize: T.fsBase, color: T.dark, lineHeight: 1.6 }}>
          {insight}
        </div>
      </div>
    </div>
  );
}

function GovernanceRail({ governance }) {
  return (
    <div
      data-testid="workflow-governance"
      style={{
        border:       `1.5px dashed ${T.border}`,
        borderRadius: 8,
        padding:      "14px 18px",
        marginBottom: 24,
        background:   T.lightGray,
      }}
    >
      <div
        style={{
          fontSize:   T.fsSm,
          fontWeight: T.fwSemibold,
          color:      T.mid,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          marginBottom: 10,
        }}
      >
        Governance
      </div>
      <ol style={{ margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: 6 }}>
        {governance.map((stmt, i) => (
          <li key={i} style={{ fontSize: T.fsBase, color: T.dark, lineHeight: 1.6 }}>
            {stmt}
          </li>
        ))}
      </ol>
    </div>
  );
}

const WORKFLOW_STATUS_LEGEND = [
  { label: "Live",      indicator: { color: T.statusLive } },
  { label: "Partial",   indicator: { color: T.amber      } },
  { label: "Not built", indicator: { color: T.errorRed   } },
];

const WORKFLOW_CHIP_LEGEND = [
  { label: "Wired",          indicator: { color: T.blue } },
  { label: "Not yet wired",  indicator: { color: T.mid  } },
];

function Legend() {
  return (
    <div
      data-testid="workflow-legend"
      style={{
        display:       "flex",
        flexDirection: "column",
        gap:           4,
        padding:       "12px 0",
        borderTop:     `1px solid ${T.border}`,
        marginTop:     8,
      }}
    >
      {/* Status dots */}
      <ViewScaffold
        legendItems={WORKFLOW_STATUS_LEGEND}
        surface="neutral"
        testid="workflow-legend-status"
      />

      {/* Actor-badge meanings — rendered via controls slot */}
      <ViewScaffold
        legendItems={[]}
        surface="neutral"
        testid="workflow-legend-actors"
        controls={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {["ai", "human", "system"].map((a) => (
              <ActorBadge key={a} actor={a} />
            ))}
          </div>
        }
      />

      {/* Chip meanings */}
      <ViewScaffold
        legendItems={WORKFLOW_CHIP_LEGEND}
        surface="neutral"
        testid="workflow-legend-chips"
      />
    </div>
  );
}

function NotYetMapped({ objectLabel }) {
  return (
    <div
      data-testid="workflow-not-yet-mapped"
      style={{
        display:        "flex",
        flexDirection:  "column",
        alignItems:     "center",
        justifyContent: "center",
        padding:        "60px 24px",
        background:     T.white,
        border:         `1px solid ${T.border}`,
        borderRadius:   10,
        textAlign:      "center",
        gap:            12,
      }}
    >
      <span style={{ fontSize: T.fsXl6, color: T.light }}>—</span>
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark }}>
        Not yet mapped
      </div>
      <div style={{ fontSize: T.fsBase, color: T.mid, maxWidth: 380, lineHeight: 1.6 }}>
        The {objectLabel} workflow hasn&apos;t been defined yet. Add an entry
        to <code>workflowDefinitions.js</code> to populate this view.
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function WorkflowView({ definition, objectLabel }) {
  if (!definition) {
    return <NotYetMapped objectLabel={objectLabel || "selected object"} />;
  }

  const stats = computeSummaryStats(definition);

  return (
    <div data-testid="workflow-view">
      <StatStrip stats={stats} objectName={definition.object} />
      <Sourcesband sources={definition.sources} />
      <Pipeline steps={definition.steps} />
      <InsightCallout insight={definition.insight} />
      <GovernanceRail governance={definition.governance} />
      <Legend />
    </div>
  );
}
